import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

export default function SearchableRoleDropdown({
  roles = [],
  value,
  onChange,
  placeholder = "Select your target role...",
  searchPlaceholder = "Search roles...",
  testId = "role-dropdown",
  fetchOptions = null,   // optional async (query) => string[] for server-side search
  allowCustom = false,   // let the user use their typed text as the role if not listed
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [asyncResults, setAsyncResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter roles based on search (static list) OR use async results from the server.
  const filteredRoles = useMemo(() => {
    if (fetchOptions) return asyncResults;
    if (!search.trim()) return roles;
    const q = search.toLowerCase();
    return roles.filter(r => r.toLowerCase().includes(q));
  }, [search, roles, fetchOptions, asyncResults]);

  // Debounced server-side search when fetchOptions is provided.
  useEffect(() => {
    if (!fetchOptions) return;
    const q = search.trim();
    if (!q) { setAsyncResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { setAsyncResults((await fetchOptions(q)) || []); }
      catch { setAsyncResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search, fetchOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (role) => {
    onChange(role);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef} data-testid={testId}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-4 py-3.5 
          rounded-xl border-2 transition-all duration-200 text-left
          ${isOpen 
            ? 'border-[#2832C2] bg-white ring-2 ring-[#2832C2]/10' 
            : 'border-[#e5e7eb] bg-white hover:border-[#2832C2]/50'
          }
        `}
        data-testid={`${testId}-trigger`}
      >
        <span className={`text-sm font-medium ${value ? 'text-[#0A1128]' : 'text-[#99a1af]'}`}>
          {value || placeholder}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-[#99a1af] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div 
          className="absolute z-50 w-full mt-2 bg-white border-2 border-[#e5e7eb] rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid={`${testId}-panel`}
        >
          {/* Search Input */}
          <div className="p-3 border-b border-[#e5e7eb]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#99a1af]" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] text-[#0A1128] text-sm placeholder:text-[#99a1af] focus:outline-none focus:border-[#2832C2] focus:bg-white transition-colors"
                data-testid={`${testId}-search`}
              />
            </div>
            <p className="text-xs text-[#99a1af] mt-2 px-1">
              {fetchOptions && !search.trim()
                ? "Type to search 5,000+ roles"
                : loading
                  ? "Searching…"
                  : `${filteredRoles.length} role${filteredRoles.length !== 1 ? 's' : ''} found`}
            </p>
          </div>

          {/* Options List */}
          <div className="max-h-[280px] overflow-y-auto" data-testid={`${testId}-options`}>
            {filteredRoles.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#99a1af] text-sm">
                {fetchOptions && !search.trim()
                  ? "Start typing to search roles…"
                  : loading
                    ? "Searching…"
                    : allowCustom && search.trim()
                      ? "No match — but you can use exactly what you typed:"
                      : "No roles match your search. Try a different keyword."}
              </div>
            ) : (
              filteredRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => handleSelect(role)}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 text-left text-sm
                    transition-colors duration-150 cursor-pointer
                    ${value === role 
                      ? 'bg-[#2832C2]/5 text-[#2832C2] font-medium' 
                      : 'text-[#0A1128] hover:bg-[#f3f4f6]'
                    }
                  `}
                  data-testid={`${testId}-option-${role.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  <span>{role}</span>
                  {value === role && <Check className="w-4 h-4 text-[#2832C2]" />}
                </button>
              ))
            )}
          </div>

          {/* Custom role: every job title can't be listed — use the typed text */}
          {allowCustom && search.trim().length >= 3 &&
            !filteredRoles.some(r => r.toLowerCase() === search.trim().toLowerCase()) && (
            <div className="border-t border-[#e5e7eb] p-2">
              <button
                type="button"
                onClick={() => handleSelect(search.trim())}
                className="w-full text-left px-3 py-2.5 rounded-lg text-sm bg-[#2832C2]/5 text-[#2832C2] font-medium hover:bg-[#2832C2]/10 transition-colors cursor-pointer"
                data-testid={`${testId}-custom-option`}
              >
                ➕ My role isn't listed — use "{search.trim()}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
